import {
  AlignmentNavigationCapability,
  ClippingCapability,
  FrustumCapability,
  ImageCapabilityBase,
  ImageCapabilityOpacityOptions,
  CrossPaneFrustumsCapability,
  ImagePointcloudCapability,
  MapNavigationCapability,
  MapStationCapability,
  NavigationalCapabilityBase,
  PanoramaCapability,
  PlanarCapability,
  StationNavigationController,
  StationsNavigationCapability,
  Web3dSettings,
} from '@trimble/web3d-capabilities';
import {
  CapabilitiesCollection,
  CapabilityCoordinatorBase,
  CapabilityRegistryContainer,
  Constructor,
  NavigationCapabilityCoordinator,
} from '@trimble/web3d';
import {
  CameraStations,
  CapabilityProviderBase,
  PointCloudCapabilityProvider,
  StationsCapabilityProvider,
  TrimBimCapabilityProvider,
  TrajectoryMetadataProvider,
  ViewsCapabilityProvider,
} from '@trimble/web3d-providers';
import { offerStationPickToActiveNavigation } from '@trimble/pane-sync';
import {
  Accessor,
  createMemo,
  createRoot,
  createSignal,
  getOwner,
  runWithOwner,
  Setter,
} from 'solid-js';
import { UnreachableCaseError } from 'ts-essentials';
import { ThreeJsManager, Web3DViewerWrapper } from '@trimble/web3d-utils';
import { ViewMode, viewModeEquals } from '../../view-mode';
import { createSequentialEffect } from '@trimble/solidjs-utils';
import {
  IDataLoadingProvider,
  IDataLoadingService,
} from '@trimble/data-loading';
import { Point2D } from '@trimble/utilities';
import { createImageStationOpacityOptions } from './station-transparency-helpers';
import { PaneVisibilityCoordinator } from './pane-visibility-coordinator';

interface ViewModeCoordinatorDependencies {
  threeJsManager: ThreeJsManager;
  dataLoadingService: IDataLoadingService;
  web3dSettings: Accessor<Web3dSettings>;
  datasetCsib: Accessor<string | undefined>;
  /**
   * World-coordinate center of the currently loaded dataset, owned by the
   * load-flow layer (DataContext.currentLoadedCenter). Forwarded into the
   * 2D TileMap coordinator so the 2D pane never needs to register the local
   * PCProvider with DLS (which would load a hidden Potree model just to
   * derive a center).
   */
  datasetCenter: Accessor<Point2D | undefined>;
  getViewsProvider(): ViewsCapabilityProvider | undefined;
  /** Injected to avoid web3d → providers, which would cycle the Nx build graph. */
  getGaussianSplatProvider?(): IDataLoadingProvider | undefined;
}

export class ViewModeCapabilityCoordinator extends CapabilityCoordinatorBase<CapabilityRegistryContainer> {
  public readonly viewMode: Accessor<ViewMode>;
  public readonly setViewMode: Setter<ViewMode>;

  public readonly imageCapabilities: Accessor<
    CapabilitiesCollection<ImageCapabilityBase> | undefined
  >;

  private readonly stationNavigationControllerSignal: Accessor<
    StationNavigationController | undefined
  >;
  private readonly setStationNavigationController: Setter<
    StationNavigationController | undefined
  >;

  private readonly currentCameraIndexSignal: Accessor<number | null>;

  private readonly contentUpdatedSignal: Accessor<void>;
  private readonly triggerContentUpdate: Setter<void>;

  private readonly dispose: () => void;
  private readonly imageStationOpacityOptions: ImageCapabilityOpacityOptions;

  constructor(
    protected readonly registryContainer: CapabilityRegistryContainer,
    initialViewMode: ViewMode,
    private readonly deps: ViewModeCoordinatorDependencies,
    private readonly viewerWrapper: Web3DViewerWrapper
  ) {
    super(registryContainer);

    const [vm, setVm] = createSignal<ViewMode>(initialViewMode, {
      equals: viewModeEquals,
    });
    this.viewMode = vm;
    this.setViewMode = setVm;

    // Bridge per-pane visibility state into the image plugin opacity hook.
    // Panorama panes consume the `stations` slider (linked to global
    // stations transparency); planar panes consume the per-pane-only
    // `image` slider (no global counterpart). View-mode-aware routing is
    // done inside the helper so both branches stay in one place.
    this.imageStationOpacityOptions = createImageStationOpacityOptions(
      () => registryContainer.get(PaneVisibilityCoordinator)(),
      () => this.viewMode()
    );

    const [stationNavigationController, setStationNavigationController] =
      createSignal<StationNavigationController | undefined>();
    this.stationNavigationControllerSignal = stationNavigationController;
    this.setStationNavigationController = setStationNavigationController;

    this.imageCapabilities = this.registryContainer.get(
      CapabilitiesCollection<ImageCapabilityBase>
    );

    this.currentCameraIndexSignal = createMemo(() => {
      const vm = this.viewMode();
      return vm.type === 'Image' ? vm.cameraIndex : null;
    });

    const [contentUpdated, triggerContentUpdate] = createSignal<void>(
      undefined,
      {
        equals: false,
      }
    );
    this.contentUpdatedSignal = contentUpdated;
    this.triggerContentUpdate = triggerContentUpdate;

    this.dispose = createRoot((dispose) => {
      createSequentialEffect(
        this.viewMode,
        async (currVm, prevVm) => {
          const owner = getOwner();

          // Preserve the station index only when switching between image-based modes
          // (Image ↔ Panorama). When coming from 3D or 2D, use the default station
          // selection because the 3D station index doesn't map to image station data
          // (e.g. planar data is often only available from the second station onward).
          const isImageBasedMode = (vm: ViewMode): boolean =>
            vm.type === 'Image' || vm.type === 'Panorama';

          const preservedStationIndex =
            prevVm != null && isImageBasedMode(prevVm)
              ? (this.stationNavigationControllerSignal()?.getCurrentStationIndex() ??
                null)
              : null;

          await Promise.all(
            this.registryContainer.capabilities().map((cap) => cap.disable())
          );

          this.triggerContentUpdate();

          await unregisterCapabilities(
            this.registryContainer,
            this.setStationNavigationController
          );

          await this.unregisterContentProviders();

          const stationsP = this.registryContainer.get(
            StationsCapabilityProvider
          )();

          runWithOwner(owner, () => {
            this.handleViewModeSwitch(currVm, stationsP, preservedStationIndex);
          });
        },
        { equals: viewModeEquals }
      );
      return dispose;
    });
  }

  public get stationNavigationController(): Accessor<
    StationNavigationController | undefined
  > {
    return this.stationNavigationControllerSignal;
  }

  public get currentCameraIndex(): Accessor<number | null> {
    return this.currentCameraIndexSignal;
  }

  public get contentUpdated(): Accessor<void> {
    return this.contentUpdatedSignal;
  }

  public async cleanup(): Promise<void> {
    this.dispose();
    await unregisterCapabilities(
      this.registryContainer,
      this.setStationNavigationController
    );
    await this.unregisterContentProviders();
    return Promise.resolve();
  }

  private async unregisterContentProviders(): Promise<void> {
    await unregisterProviders(
      this.registryContainer,
      this.deps.dataLoadingService,
      [
        PointCloudCapabilityProvider,
        TrimBimCapabilityProvider,
        TrajectoryMetadataProvider,
      ]
    );

    const gaussianSplatProvider = this.deps.getGaussianSplatProvider?.();
    if (gaussianSplatProvider) {
      await this.deps.dataLoadingService.unregisterProvider(
        gaussianSplatProvider
      );
    }
  }

  /**
   * Resolved lazily: the navigation coordinator is registered independently of
   * this coordinator, so the lookup must happen at call time.
   */
  private activeNavigationCapability(): NavigationalCapabilityBase | undefined {
    return this.registryContainer
      .get(NavigationCapabilityCoordinator)()
      ?.activeNavigationCapability();
  }

  private handleViewModeSwitch(
    vm: ViewMode,
    stationsP: StationsCapabilityProvider | undefined,
    preservedStationIndex: number | null
  ): void {
    const pcProvider = this.registryContainer.get(
      PointCloudCapabilityProvider
    )();
    const gsProvider = this.deps.getGaussianSplatProvider?.();
    const trbProvider = this.registryContainer.get(TrimBimCapabilityProvider)();

    switch (vm.type) {
      case '3D':
        this.setup3DMode(pcProvider, gsProvider, trbProvider, stationsP);
        break;
      case 'Image':
        this.setupImageBasedMode(
          'planar',
          vm.cameraIndex,
          pcProvider,
          trbProvider,
          stationsP,
          preservedStationIndex
        );
        break;
      case 'Panorama':
        this.setupImageBasedMode(
          'panorama',
          0,
          pcProvider,
          trbProvider,
          stationsP,
          preservedStationIndex
        );
        break;
      case '2D':
        this.setup2DMode(stationsP);
        break;
      default:
        throw new UnreachableCaseError(vm);
    }
  }

  /**
   * TrimBIM carries both designs and trajectory geometry, and
   * `createTrimBimConfig` resolves the metadata provider by viewer, so the
   * pane's `TrajectoryMetadataProvider` has to be registered alongside it or
   * trajectory loading throws.
   */
  private registerModelProviders(
    trbProvider: TrimBimCapabilityProvider
  ): TrajectoryMetadataProvider | undefined {
    this.deps.dataLoadingService.registerProvider(trbProvider);

    const trajectoryMetadataProvider = this.registryContainer.get(
      TrajectoryMetadataProvider
    )();
    if (trajectoryMetadataProvider) {
      this.deps.dataLoadingService.registerProvider(trajectoryMetadataProvider);
    }

    return trajectoryMetadataProvider;
  }

  private setup3DMode(
    pcProvider: PointCloudCapabilityProvider | undefined,
    gsProvider: IDataLoadingProvider | undefined,
    trbProvider: TrimBimCapabilityProvider | undefined,
    stationsP: StationsCapabilityProvider | undefined
  ): void {
    if (pcProvider) {
      pcProvider.setIsPointCloudsVisible(true);
      this.deps.dataLoadingService.registerProvider(pcProvider);
    }
    if (gsProvider) {
      this.deps.dataLoadingService.registerProvider(gsProvider);
    }
    const clippingProviders = [pcProvider, trbProvider].filter(
      (p): p is PointCloudCapabilityProvider | TrimBimCapabilityProvider =>
        p != null
    );
    this.registryContainer.register(
      ClippingCapability,
      new ClippingCapability(this.viewerWrapper.viewer, clippingProviders)
    );

    if (trbProvider) {
      const trajectoryMetadataProvider =
        this.registerModelProviders(trbProvider);

      if (trajectoryMetadataProvider) {
        this.registryContainer.register(
          AlignmentNavigationCapability,
          new AlignmentNavigationCapability(
            this.viewerWrapper.viewer,
            trbProvider,
            trajectoryMetadataProvider
          )
        );
      }
    }

    if (stationsP) {
      const externalFrustumsCap = this.registryContainer.register(
        CrossPaneFrustumsCapability,
        new CrossPaneFrustumsCapability(
          this.viewerWrapper.viewer,
          this.deps.threeJsManager,
          { useMapHeight: false, renderStyle: 'cone' }
        )
      );
      externalFrustumsCap.enable();
      externalFrustumsCap.activate();

      const alignmentNavigationCapability = this.registryContainer.get(
        AlignmentNavigationCapability
      )();

      const canShowStationMarkers = createMemo(
        () => !(alignmentNavigationCapability?.crossSectionEnabled() ?? false)
      );

      // Annotated because the pick callback closes over this binding.
      const stationNavCap: StationsNavigationCapability =
        this.registryContainer.register(
          StationsNavigationCapability,
          new StationsNavigationCapability(
            this.viewerWrapper.viewer,
            stationsP,
            this.deps.threeJsManager,
            canShowStationMarkers,
            undefined,
            (position) =>
              offerStationPickToActiveNavigation(
                this.activeNavigationCapability(),
                stationNavCap,
                position
              )
          )
        );
      stationNavCap.enable();
      this.setStationNavigationController(stationNavCap);
    }
  }

  private setupImageBasedMode(
    mode: 'planar' | 'panorama',
    cameraIndex: number,
    pcProvider: PointCloudCapabilityProvider | undefined,
    trbProvider: TrimBimCapabilityProvider | undefined,
    stationsP: StationsCapabilityProvider | undefined,
    preservedStationIndex: number | null
  ): void {
    // Clip distance: prefer the pane's PaneVisibilityCoordinator (which
    // either follows global/upstream when linked or returns the user's
    // local override). Fall back to the settings value when the pane
    // capability hasn't been registered yet.
    // Debounced: each settled value rebuilds the clip box. At 0 the cloud
    // is hidden (root.visible) rather than unloaded; once loaded it stays
    // registered until the pane leaves image/panorama mode.
    const clipDistance: Accessor<number> = (): number =>
      this.registryContainer
        .get(PaneVisibilityCoordinator)()
        ?.clipDistance.effectiveDebounced() ??
      this.deps.web3dSettings()?.imageViewPointcloudClippingDistance ??
      0;

    const imagePointcloudCapability = this.registryContainer.register(
      ImagePointcloudCapability,
      new ImagePointcloudCapability(this.viewerWrapper.viewer, clipDistance)
    );

    imagePointcloudCapability.enable();

    let pcProviderRegistered = false;
    let trbProviderRegistered = false;

    createSequentialEffect(clipDistance, async (distance) => {
      if (distance <= 0) {
        // Models are hidden by the models effect in ViewsLoadingCoordinator,
        // which owns model presentation for the pane.
        if (pcProvider && pcProviderRegistered) {
          pcProvider.setIsPointCloudsVisible(false);
        }
        return;
      }

      if (pcProvider) {
        if (!pcProviderRegistered) {
          this.deps.dataLoadingService.registerProvider(pcProvider);
          pcProviderRegistered = true;
        }
        pcProvider.setIsPointCloudsVisible(true);
      }

      if (trbProvider && !trbProviderRegistered) {
        this.registerModelProviders(trbProvider);
        trbProviderRegistered = true;
      }
    });

    if (stationsP) {
      const liveCapabilities = new Map<string, ImageCapabilityBase>();

      const filterCamera = (camera: CameraStations): boolean =>
        mode === 'panorama'
          ? camera.kind === 'panorama'
          : camera.kind !== 'panorama';

      const cameraNameSetEquals = (
        current: CameraStations[],
        prev: CameraStations[] | undefined
      ): boolean => {
        if (!prev) return false;
        const currentNames = current
          .filter(filterCamera)
          .map((c) => c.cameraName)
          .sort();
        const prevNames = prev
          .filter(filterCamera)
          .map((c) => c.cameraName)
          .sort();
        return (
          currentNames.length === prevNames.length &&
          currentNames.every((n, i) => n === prevNames[i])
        );
      };

      createSequentialEffect(
        stationsP.items,
        async (cameraStations) => {
          const filteredCameras = cameraStations
            .filter(filterCamera)
            .sort((a, b) => a.cameraName.localeCompare(b.cameraName));

          const newCameraNames = new Set(
            filteredCameras.map((c) => c.cameraName)
          );

          // Dispose capabilities for removed cameras
          for (const [name, cap] of liveCapabilities) {
            if (!newCameraNames.has(name)) {
              await cap.cleanup();
              liveCapabilities.delete(name);
            }
          }

          // Create capabilities only for newly added cameras
          for (const camera of filteredCameras) {
            if (!liveCapabilities.has(camera.cameraName)) {
              const imgCap =
                mode === 'panorama'
                  ? this.registryContainer.register(
                      PanoramaCapability,
                      new PanoramaCapability(
                        stationsP,
                        this.viewerWrapper.viewer,
                        this.viewerWrapper.panoramaPlugin,
                        camera.cameraName,
                        this.deps.web3dSettings,
                        this.deps.threeJsManager,
                        this.imageStationOpacityOptions
                      )
                    )
                  : this.registryContainer.register(
                      PlanarCapability,
                      new PlanarCapability(
                        stationsP,
                        this.viewerWrapper.viewer,
                        this.viewerWrapper.planarPlugin,
                        camera.cameraName,
                        this.deps.web3dSettings,
                        this.deps.threeJsManager,
                        this.imageStationOpacityOptions
                      )
                    );
              imgCap.enable();
              liveCapabilities.set(camera.cameraName, imgCap);
            }
          }

          // Build sorted capability list matching camera order and register
          const imgCaps = filteredCameras
            .map((c) => liveCapabilities.get(c.cameraName))
            .filter((cap): cap is ImageCapabilityBase => cap !== undefined);

          this.registryContainer.register(
            CapabilitiesCollection<ImageCapabilityBase>,
            new CapabilitiesCollection(imgCaps)
          );

          const activeCap = imgCaps[cameraIndex];
          if (!activeCap) {
            return;
          }

          // Publish synchronously so view-restore callers don't see a stale cap.
          this.setStationNavigationController(activeCap);

          queueMicrotask(() => {
            activeCap.activate();

            // Skip if a station was already selected (e.g. by view restore).
            if (activeCap.selectedStationIndex() != null) return;

            const stationCount = stationsP.stationCount();
            if (
              preservedStationIndex != null &&
              preservedStationIndex >= 0 &&
              preservedStationIndex < stationCount
            ) {
              activeCap.goToStation(preservedStationIndex);
            } else if (stationCount > 1) {
              activeCap.goToStation(1);
            } else if (stationCount > 0) {
              activeCap.goToStation(0);
            }
          });
          this.triggerContentUpdate();
        },
        { equals: cameraNameSetEquals }
      );
    }
  }

  private setup2DMode(stationsP: StationsCapabilityProvider | undefined): void {
    // csib and center come from DataContext; no need to register PCProvider in 2D mode.
    this.registryContainer.register(
      FrustumCapability,
      new FrustumCapability(this.viewerWrapper.viewer, this.deps.threeJsManager)
    );
    const externalFrustumsCap2D = this.registryContainer.register(
      CrossPaneFrustumsCapability,
      new CrossPaneFrustumsCapability(
        this.viewerWrapper.viewer,
        this.deps.threeJsManager,
        { useMapHeight: true, renderStyle: 'flat' }
      )
    );
    externalFrustumsCap2D.enable();
    externalFrustumsCap2D.activate();

    if (stationsP) {
      const mapStationCap = this.registryContainer.register(
        MapStationCapability,
        new MapStationCapability(
          this.viewerWrapper.viewer,
          stationsP,
          this.deps.threeJsManager
        )
      );
      mapStationCap.enable();
    }

    const mapNavCapability = this.registryContainer.register(
      MapNavigationCapability,
      new MapNavigationCapability(
        this.viewerWrapper.viewer,
        this.deps.threeJsManager
      )
    );
    mapNavCapability.enable();
    mapNavCapability.activate();
  }
}

async function unregisterCapabilities(
  registryContainer: CapabilityRegistryContainer,
  setStationNavigationController: Setter<
    StationNavigationController | undefined
  >
): Promise<void> {
  setStationNavigationController(undefined);
  await Promise.all([
    registryContainer.unregister(CapabilitiesCollection<ImageCapabilityBase>),
    registryContainer.unregister(ClippingCapability),
    registryContainer.unregister(StationsNavigationCapability),
    registryContainer.unregister(AlignmentNavigationCapability),
    registryContainer.unregister(FrustumCapability),
    registryContainer.unregister(CrossPaneFrustumsCapability),
    registryContainer.unregister(MapNavigationCapability),
    registryContainer.unregister(MapStationCapability),
    registryContainer.unregister(ImagePointcloudCapability),
  ]);
}

async function unregisterProviders(
  registryContainer: CapabilityRegistryContainer,
  dataLoadingService: IDataLoadingService,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  providers: Constructor<CapabilityProviderBase<any>>[]
): Promise<void> {
  await Promise.all(
    providers.map(async (providerClass) => {
      const provider = registryContainer.get(providerClass)();
      if (provider) {
        await dataLoadingService.unregisterProvider(provider);
      }
    })
  );
}
